<template>
  <div>
    <v-dialog
      v-model="dialog"
      persistent
      eager
      scrollable
      max-width="1000px"
      content-class="rounded-dialog"
    >
      <v-form ref="PerformanceTarget" @submit.prevent>
        <v-card>
          <v-card-title dark class="dialog-header">
            <div class="dialog-header-content">
              <div class="dialog-title-wrapper">
                <div class="dialog-icon">
                  <icon name="circle-add" />
                </div>

                <div class="dialog-text">
                  <div class="dialog-title">{{ action }} OPCR Target</div>

                  <div class="dialog-subtitle">
                    Fill in the required information below
                  </div>
                </div>
              </div>

              <v-btn icon class="close-btn" @click="closeD()">
                <icon name="circle-close" />
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text style="max-height: 700px" class="my-4">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <div class="rating-card">
                    <div class="rating-header">
                      <div class="rating-title">
                        <v-icon size="18" color="#147452" class="mr-2">
                          mdi-star-outline
                        </v-icon>
                        Rating Criteria
                      </div>

                      <div class="rating-subtitle">
                        Uncheck criteria that should not be included in the
                        evaluation.
                      </div>
                    </div>

                    <v-row class="mt-2">
                      <v-col cols="12" md="4">
                        <div
                          class="metric-card"
                          :class="{ active: eff_is_rated }"
                        >
                          <v-switch
                            v-model="eff_is_rated"
                            hide-details
                            class="metric-checkbox ma-0"
                            color="#147452"
                          />

                          <div class="metric-content">
                            <div class="metric-code">E</div>
                            <div class="metric-title">Efficiency</div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12" md="4">
                        <div
                          class="metric-card"
                          :class="{ active: qly_is_rated }"
                        >
                          <v-switch
                            v-model="qly_is_rated"
                            hide-details
                            class="metric-checkbox ma-0"
                            color="#147452"
                          />

                          <div class="metric-content">
                            <div class="metric-code">Q</div>
                            <div class="metric-title">Quality</div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12" md="4">
                        <div
                          class="metric-card"
                          :class="{ active: timeliness_is_rated }"
                        >
                          <v-switch
                            v-model="timeliness_is_rated"
                            hide-details
                            class="metric-checkbox ma-0"
                            color="#147452"
                          />

                          <div class="metric-content">
                            <div class="metric-code">T</div>
                            <div class="metric-title">Timeliness</div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    label="Major Final Output"
                    outlined
                    v-model="mfo"
                    dense
                    :rules="[formRules.required]"
                    class="rounded-lg"
                    color="#6DB249"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <div class="indicator-card">
                    <!-- Header -->
                    <div class="indicator-header">
                      <div class="indicator-title">
                        <v-icon size="18" color="#147452" class="mr-2">
                          mdi-chart-box-outline
                        </v-icon>
                        Success Indicator
                      </div>

                      <div class="indicator-subtitle">
                        Configure target measures and rating criteria.
                      </div>
                    </div>

                    <!-- Metrics -->
                    <!-- Metrics -->
                    <v-row class="mt-2">
                      <!-- Efficiency - Full Row -->
                      <v-col cols="12">
                        <div class="metric-section">
                          <div class="section-label">Efficiency</div>

                          <v-radio-group
                            class="efficiency-radio-group"
                            :rules="[formRules.required]"
                            v-model="efficiency_type"
                            mandatory
                            @change="setEfficiency()"
                            dense
                            row
                            hide-details
                          >
                            <v-radio
                              v-for="i in efficiencyTypeList"
                              :key="i.value"
                              :label="i.desc"
                              :value="i.value"
                            />
                          </v-radio-group>

                          <v-text-field
                            label="Target Value"
                            outlined
                            type="number"
                            v-model="efficiency"
                            :rules="[
                              formRules.required,
                              formRules.numberRequired,
                            ]"
                            color="#147452"
                            class="mt-2"
                          />
                        </div>
                      </v-col>

                      <!-- Quality -->
                      <v-col cols="12" md="6" v-if="qly_is_rated">
                        <div class="metric-section">
                          <div class="section-label1">Quality</div>

                          <v-autocomplete
                            v-model="quality"
                            outlined
                            item-text="description"
                            item-value="id"
                            label="Select Quality"
                            color="#147452"
                            :items="qlyList"
                          />
                        </div>
                      </v-col>

                      <!-- Timeliness -->
                      <v-col cols="12" md="6" v-if="timeliness_is_rated">
                        <div class="metric-section">
                          <div class="section-label1">Timeliness</div>

                          <v-autocomplete
                            v-model="timeliness_selected"
                            outlined
                            item-text="description"
                            item-value="id"
                            label="Select Timeliness"
                            color="#147452"
                            :items="timelinessList"
                            @change="changeTimeliness()"
                          />

                          <!-- <v-text-field
                            v-if="isToTimelinessNumber"
                            label="Number of day/s, weeks, etc."
                            outlined
                            type="number"
                            v-model="t_num"
                            :rules="
                              isToTimelinessNumber
                                ? [formRules.required, formRules.numberRequired]
                                : []
                            "
                            dense
                            color="#147452"
                            class="mt-3"
                          /> -->

                          <!-- <div
                            v-if="timeliness_selected"
                            class="mt-2 text-caption orange--text"
                          >
                            <i>Additional description is optional.</i>
                          </div> -->

                          <!-- <v-text-field
                            v-if="timeliness_selected"
                            label="Timeliness Additional Text"
                            outlined
                            v-model="t_add_desc"
                            dense
                            color="#147452"
                            class="mt-2"
                          /> -->
                        </div>
                      </v-col>
                    </v-row>

                    <!-- Preview -->
                    <div class="preview-section">
                      <div class="preview-label">Success Indicator Preview</div>

                      <div class="preview-note">
                        <v-icon size="14" color="#2196F3">
                          mdi-information-outline
                        </v-icon>

                        Edit the generated success indicator if necessary.
                      </div>

                      <v-textarea
                        v-model="suc_ind_preview"
                        outlined
                        rows="4"
                        dense
                        color="#147452"
                        class="mt-3"
                      />
                    </div>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  v-if="isTargetNeedForTagging && getPositionToTag.canTag"
                >
                  <div class="rating-card">
                    <div class="d-flex justify-space-between my-4 items-center">
                      <div class="rating-header">
                        <div class="rating-title">
                          <v-icon size="18" color="#147452" class="mr-2">
                            mdi-tag-outline
                          </v-icon>
                          <span>
                            {{ getPositionToTag.label }} &nbsp;
                            <span class="primary--text">
                              {{ getPositionToTag.toTag }}</span
                            >
                          </span>
                        </div>

                        <div class="rating-subtitle">
                          Assign and manage units associated with this position.
                        </div>
                      </div>
                      <div class="text-subtitle-1">
                        <v-btn
                          @click="add_head_tagging()"
                          class="btn-save"
                          color="#519043"
                        >
                          Add
                        </v-btn>
                      </div>
                    </div>

                    <v-card class="dt-container mt-4" elevation="0" outlined>
                      <v-data-table
                        :headers="tagged_head_headers"
                        :items="tagged_head_data"
                        hide-default-footer
                      >
                        <template v-slot:[`item.target`]="{ item }">
                          <div>
                            {{ item.target }}
                          </div>
                        </template>

                        <template v-slot:[`item.action`]="{ item, index }">
                          <div class="button-group pa-2">
                            <v-btn
                              class="global-btn-save"
                              @click="edit_tag(item, index)"
                              >Edit</v-btn
                            >
                            <v-btn class="global-btn-save" @click="untag(item)"
                              >Untag</v-btn
                            >
                          </div>
                        </template>
                      </v-data-table>
                    </v-card>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-actions class="pa-5">
            <v-spacer></v-spacer>

            <v-btn class="btn-cancel" outlined @click="closeD()">
              Cancel
            </v-btn>

            <v-btn
              v-if="action == 'Add'"
              class="btn-save"
              @click="addnew()"
              :disabled="isSubmitLoading"
              :loading="isSubmitLoading"
            >
              Save
            </v-btn>
            <v-btn
              v-if="action == 'Update'"
              color="#519043"
              class="btn-save"
              @click="update()"
              :disabled="isSubmitLoading"
              :loading="isSubmitLoading"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- heads users dialog-->
    <v-dialog
      v-model="headsDialog"
      eager
      persistent
      scrollable
      max-width="500px"
      content-class="rounded-dialog"
    >
      <v-card>
        <v-card-title dark class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-wrapper">
              <div class="dialog-icon">
                <icon name="circle-add" />
              </div>

              <div class="dialog-text">
                <div class="dialog-title">
                  {{
                    isTargetNeedForTagging
                      ? "Select Head/Offices "
                      : "Select Personnel"
                  }}
                </div>

                <div class="dialog-subtitle">
                  Fill in the required information belows
                </div>
              </div>
            </div>

            <v-btn icon class="close-btn" @click="closeHeadsDialog()">
              <icon name="circle-close" />
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text style="max-height: 700px" class="my-4">
          <v-form ref="HeadsDialogRef" @submit.prevent>
            <v-col cols="12">
              <v-autocomplete
                v-model="tagged_head_temp_data"
                :disabled="tagHeadAction == 'Update'"
                label="Head of Office"
                outlined
                class="rounded-lg"
                item-text="name"
                item-value="usID"
                color="#93CB5B"
                :items="tempHeadsList"
              >
                <template v-slot:selection="data">
                  <v-chip
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    small
                    @click="data.select"
                    @click:close="removeTaggedOnClick(data.item)"
                  >
                    {{ data.item.name }}
                  </v-chip>
                </template>
                <template v-slot:item="data">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title
                        v-html="data.item.name"
                      ></v-list-item-title>
                      <v-list-item-subtitle
                        v-html="data.item.office"
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" v-if="isTargetNeedForTagging">
              <v-text-field
                v-model="head_target"
                type="number"
                :rules="[formRules.required]"
                outlined
                required
                label="Target"
                class="rounded-lg"
                color="#6DB249"
              ></v-text-field>
            </v-col>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-5">
          <v-spacer></v-spacer>
          <v-btn class="btn-cancel" outlined @click="closeHeadsDialog()">
            Cancel
          </v-btn>
          <v-btn
            color="#519043"
            class="btn-save"
            v-if="tagHeadAction == 'Add'"
            :loading="targetBtnLoading"
            :disabled="targetBtnLoading"
            @click="saveTaggedHeads()"
          >
            Add
          </v-btn>
          <v-btn
            color="#519043"
            class="btn-save"
            v-if="tagHeadAction == 'Update'"
            @click="updateTaggedHeads()"
            :loading="targetBtnLoading"
            :disabled="targetBtnLoading"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- untag dialog -->
    <v-dialog
      v-if="unTagDialog"
      v-model="unTagDialog"
      persistent
      max-width="420"
      content-class="delete-dialog-content"
    >
      <v-card class="delete-dialog">
        <div class="delete-content">
          <div class="submit-icon">
            <icon name="circle-check" />
          </div>

          <div class="delete-title">Warning!</div>

          <div class="delete-description">
            Are you sure you want to untag
            <strong>{{ unTagUser.head_user.name }}</strong
            >?
          </div>
        </div>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            text
            color="grey darken-1"
            class="btn-cancel"
            :disabled="confirmBtnLoading"
            @click="cancelUntag()"
          >
            Cancel
          </v-btn>

          <v-spacer />

          <v-btn
            color="#147452"
            depressed
            :loading="confirmBtnLoading"
            :disabled="confirmBtnLoading"
            class="btn-save"
            @click="confirmUntag()"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :top="fadeAwayMessage.top"
      :type="fadeAwayMessage.type"
      :timeout="3000"
    ></fade-away-message-component>
  </div>
</template>

<script>
import icon from "@/assets/icon.vue";
export default {
  components: {
    icon,
  },
  props: {
    data: null,
    opcrID: null,
    action: null,
    myTaggingType: null,
  },

  data() {
    return {
      id: null,
      timeliness: null,
      timeliness_selected: null,
      t_num: null,
      t_add_desc: null,
      quality: null,
      efficiency: null,
      efficiency_type: null,
      eff_is_rated: true,
      qly_is_rated: true,
      timeliness_is_rated: true,
      dialog: false,
      qlyList: [],
      timelinessList: [],
      isSubmitLoading: false,
      confirmBtnLoading: false,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      efficiencyTypeList: [
        { value: 2, desc: "Targets with fixed maximum accomplishment" },
        { value: 1, desc: "Targets without fixed maximum accomplishment" },
      ],
      office_strat_plan: [],
      sptID: null,
      mfo: null,
      mfoDescription: null,
      papSelected: null,
      papTaggedList: [],
      itemForTagging: null,
      tagIdxToUpdate: null,
      target_to_distribute: null,
      tempHeadsList: [],
      taggingType: null,
      tagHeadAction: null,
      headsDialog: false,
      tagged_head_temp_data: null,
      targetBtnLoading: false,
      unTagUser: null,
      unTagDialog: false,
      sdp_target_format: null,
      success_indicator_to_save: null,
      tagged_head_data: [],
      head_target: null,
      usersList: [],
      untagList: [],
      tagged_head_headers: [
        {
          text: "Name",
          value: "head_user.name",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Office",
          value: "head_user.office",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Targets",
          value: "target",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Action",
          value: "action",
          align: "center",
          valign: "center",
          width: 200,
        },
      ],
    };
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        console.log("ADD OPCR", data);
        this.papSelected = null;
        console.log("My Taggung", this.myTaggingType);
        this.untagList = [];

        if (data.id) {
          this.id = data.id;
          this.mfo = data.mfo;
          this.timeliness = data.timeliness;
          this.quality = parseInt(data.quality_util_ID);
          // this.efficiency = data.QL;
          // this.efficiency_type = data.QL_type;
          this.efficiency = data.efficiency;
          this.efficiency_type = data.efficiency_type;
          this.eff_is_rated = data.QL_is_rated;
          this.qly_is_rated = data.QT_is_rated;
          this.timeliness_is_rated = data.timeliness_is_rated;
          this.t_num = data.t_num;
          this.timeliness_selected = parseInt(data.timeliness_util_id);
          this.t_add_desc = data.t_add_desc;
          this.sptID = data.linked_sptID ? data.linked_sptID : null;

          this.getTaggedOffices(
            data.id,
            data.opcr_pap_target_distributed_tagging_ID != null
              ? parseInt(data.opcr_pap_target_distributed_tagging_ID)
              : "null",
          );
        } else {
          this.id = null;
          this.mfo = null;
          this.timeliness = null;
          this.efficiency_type = null;
          this.quality = null;
          this.efficiency = null;
          this.eff_is_rated = 1;
          this.qly_is_rated = 1;
          this.timeliness_is_rated = 1;
          this.t_num = null;
          this.timeliness_selected = null;
          this.t_add_desc = null;
          this.sptID = null;
          this.tagged_head_data = [];
        }
        // this.getMyTaggedPAP(data.year);
        // this.getStratPlan();
        this.getOfficeStratPlan();
        this.getIPCRQuality();
        this.getIPCRTimeliness();
        this.getHeadUsers();
      },

      deep: true,
    },
  },
  computed: {
    // shouldEnableEff() {
    //   // data.OpcrPapTargetDistributedTaggingCount &&
    //   //                       (data.opcr_pap_target_distributed_tagging_ID !=
    //   //                         null ||
    //   //                         data.OpcrPapTargetDistributedTaggingID != null)
    //   if (this.data) {
    //     if (this.action == "Update") {
    //       return true;
    //     } else {
    //       return false;
    //     }
    //   }
    //   return false;
    // },

    isToTimelinessNumber() {
      let toAdd = false;
      if (this.timeliness_selected) {
        for (let i = 0; i < this.timelinessList.length; i++) {
          if (this.timelinessList[i].id == this.timeliness_selected) {
            toAdd = this.timelinessList[i].toAddNumber;
          }
        }
      }
      return toAdd;
    },

    timelinessPreview() {
      let text = "";
      if (this.timeliness_selected) {
        for (let i = 0; i < this.timelinessList.length; i++) {
          if (this.timelinessList[i].id == this.timeliness_selected) {
            if (this.timelinessList[i].toAddNumber) {
              text += this.t_num ? this.t_num + " " : "";
              text += this.timelinessList[i].description + " ";
              text += this.t_add_desc ? this.t_add_desc : "";
            } else {
              text += this.timelinessList[i].description + " ";
              text += this.t_add_desc ? this.t_add_desc : "";
            }
          }
        }
      }
      return text;
    },

    suc_ind_preview: {
      get() {
        let text = "";
        if (this.timeliness_selected) {
          for (let i = 0; i < this.timelinessList.length; i++) {
            if (this.timelinessList[i].id == this.timeliness_selected) {
              if (this.timelinessList[i].toAddNumber) {
                if (this.efficiency_type == 1) {
                  text += "100%" + " " + this.mfo + " ";
                } else {
                  text += this.efficiency ? this.efficiency + " " : " ";
                  text += this.mfo + " ";
                }

                text += this.quality ? this.getQualityDesc + " " : "";
                text += this.t_num ? this.t_num + " " : "";
                text += this.timelinessList[i].description + " ";
                text += this.t_add_desc ? this.t_add_desc : "";
              } else {
                if (this.efficiency_type == 1) {
                  text += "100%" + " " + this.mfo + " ";
                } else {
                  text += this.efficiency ? this.efficiency + " " : " ";
                  text += this.mfo ? this.mfo + " " : " ";
                }
                text += this.quality ? this.getQualityDesc + " " : "";
                text += this.timelinessList[i].description + " ";
                text += this.t_add_desc ? this.t_add_desc : "";
              }
            }
          }
        } else if (this.quality) {
          if (this.efficiency_type == 1) {
            text += "100%" + " " + this.mfo + " ";
          } else {
            text += this.efficiency ? this.efficiency + " " : " ";
            text += this.mfo + " ";
          }

          text += this.quality ? this.getQualityDesc + " " : "";
          text += this.t_num ? this.t_num + " " : "";
          text += this.t_add_desc ? this.t_add_desc : "";
        } else {
          if (this.efficiency_type == 1) {
            text += "100%" + " " + this.mfo + " ";
          } else {
            text += this.quality ? this.getQualityDesc + " " : "";
            text += this.efficiency ? this.efficiency + " " : " ";
            text += this.mfo + " ";
          }
        }
        return text;
      },
      set(val) {
        this.success_indicator_to_save = val;
        console.log("COMPUTE VCAL", this.success_indicator_to_save);
      },
    },

    getQualityDesc() {
      let text = "";
      if (this.quality) {
        for (let i = 0; i < this.qlyList.length; i++) {
          if (this.qlyList[i].id == this.quality) {
            text = this.qlyList[i].description;
          }
        }
      }
      return text;
    },

    getCurrentTaggedTotal() {
      let total = 0;
      for (let i = 0; i < this.tagged_head_data.length; i++) {
        total += parseInt(this.tagged_head_data[i].target);
      }
      return total;
    },

    getPositionToTag() {
      let toTag = "";
      let label = "";
      let canTag = true;
      if (this.myTaggingType && this.myTaggingType.type == "vp") {
        label = "Tag to ";
        if (this.myTaggingType.office_type != "academic affairs") {
          toTag = "Directors";
        } else {
          toTag = "Deans/Directors";
        }
      } else if (this.myTaggingType && this.myTaggingType.type == "director") {
        label = "Tag to ";
        toTag = "Unit Heads";
      } else if (this.myTaggingType && this.myTaggingType.type == "dean") {
        label = "Tag to ";
        toTag = "Program Chairpersons";
        canTag = false;
      } else if (this.myTaggingType && this.myTaggingType.type == "unit_head") {
        label = "Tag to ";
        toTag = "Personnel/s";
      } else if (
        this.myTaggingType &&
        this.myTaggingType.type == "prog_chair"
      ) {
        label = "Tag to ";
        toTag = "Faculty/Faculties";
        canTag = false;
      }
      return {
        label,
        toTag,
        canTag,
      };
    },
    isTargetNeedForTagging() {
      let needed = true;

      if (this.myTaggingType && this.myTaggingType.type == "unit_head") {
        needed = false;
      } else if (
        this.myTaggingType &&
        this.myTaggingType.type == "prog_chair"
      ) {
        needed = false;
      }
      return needed;
    },
  },

  methods: {
    initialize() {},

    setEfficiency() {
      if (this.data.efficiency) {
        this.efficiency = this.data.efficiency;
      } else {
        this.efficiency = null;
      }
    },
    removeOnClick(item) {
      const index = this.papTaggedList.indexOf(item.id);
      if (index >= 0) {
        this.papTaggedList.splice(index, 1);
        for (let i = 0; i < this.papTaggedList.length; i++) {
          if (this.papTaggedList[i].id == item.id) {
            this.papTaggedList.splice(i, 1);
          }
        }
      }
    },
    getMFODesc(id) {
      return this.mfoList[id - 1];
    },
    changeTimeliness() {
      this.t_num = null;
      this.t_add_desc = null;
    },
    closeD() {
      this.eventHub.$emit("closeAddOPCRTargetsDialog", false);
      this.initialize();
      this.isSubmitLoading = false;
      this.dialog = false;
    },

    getMyTaggedPAP(year) {
      this.axiosCall("/pap/getMyTaggedPAP/" + year, "GET").then((res) => {
        // this.office_strat_plan = res.data;
        console.log(res.data);
        this.papTaggedList = res.data;
      });
    },

    getStratPlan() {
      this.axiosCall("/strategic-planning/getCurrent", "GET").then((res) => {
        this.office_strat_plan = res.data;
      });
    },

    getOfficeStratPlan() {
      this.axiosCall("/office-strat-plan-target/getOfficeTarget", "GET").then(
        (res) => {
          this.office_strat_plan = res.data;
        },
      );
    },

    getMyFilteredUserList() {
      let filtered = [];
      let off = this.$store.state.emp.officeID;
      // console.log(
      //   "OFF",
      //   off,
      //   this.myTaggingType.office_type,
      //   this.myTaggingType.type
      // );
      if (this.myTaggingType && this.myTaggingType.type == "vp") {
        for (let i = 0; i < this.usersList.length; i++) {
          if (this.myTaggingType.office_type != "academic affairs") {
            if (
              this.usersList[i].office_underID == off &&
              this.usersList[i].roleID == 7
            ) {
              filtered.push(this.usersList[i]);
            }
          } else {
            if (
              this.usersList[i].office_underID == off &&
              (this.usersList[i].roleID == 7 || this.usersList[i].roleID == 8)
            ) {
              filtered.push(this.usersList[i]);
            }
          }
        }
      } else if (this.myTaggingType.type == "director") {
        for (let i = 0; i < this.usersList.length; i++) {
          if (this.myTaggingType.office_type == "admin and finance") {
            if (
              this.usersList[i].office_underID == off &&
              (this.usersList[i].roleID == 9 || this.usersList[i].roleID == 1)
            ) {
              filtered.push(this.usersList[i]);
            }
          } else {
            if (
              this.usersList[i].office_underID == off &&
              this.usersList[i].roleID == 9
            ) {
              filtered.push(this.usersList[i]);
            }
          }
        }
      } else if (this.myTaggingType.type == "dean") {
        for (let i = 0; i < this.usersList.length; i++) {
          if (
            (this.usersList[i].office_underID == off ||
              this.usersList[i].officeID == off) &&
            this.usersList[i].roleID == 12
          ) {
            filtered.push(this.usersList[i]);
          }
        }
      } else if (
        this.myTaggingType.type == "unit_head" ||
        this.myTaggingType.type == "prog_chair"
      ) {
        for (let i = 0; i < this.usersList.length; i++) {
          if (
            (this.usersList[i].office_underID == off ||
              this.usersList[i].officeID == off) &&
            this.usersList[i].roleID == 2
          ) {
            filtered.push(this.usersList[i]);
          }
        }
      }

      return filtered;
    },

    add_head_tagging() {
      if (this.efficiency) {
        this.tagHeadAction = "Add";
        // let list = this.getFilteredUserList;
        let list = this.getMyFilteredUserList();

        console.log(list);
        if (this.tagged_head_data.length > 0) {
          for (let i = 0; i < this.tagged_head_data.length; i++) {
            for (let j = 0; j < list.length; j++) {
              if (this.tagged_head_data[i].head_user.usID == list[j].usID) {
                list.splice(j, 1);
                break;
              }
            }
          }
          this.tempHeadsList = list;
        } else {
          this.tempHeadsList = this.getMyFilteredUserList();
        }
        this.tagged_head_temp_data = null;
        this.head_target = null;
        this.headsDialog = true;
      } else {
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.header = "System Message";
        this.fadeAwayMessage.message = "Please fill efficiency field first!";
      }
    },

    removeTaggedOnClick(item) {
      this.tagged_head_temp_data = null;

      for (let i = 0; i < this.tagged_head_data.length; i++) {
        if (this.tagged_head_data[i].head_user.usID == item.head_user.usID) {
          if (!item.id) {
            console.log("REMOVED TAGGED1", item);
            this.tagged_head_data.splice(i, 1);
          } else {
            console.log("REMOVED TAGGED123", item);
            this.untagList.push(this.tagged_head_data[i]);
            this.tagged_head_data.splice(i, 1);
          }
        }
      }
    },

    removeTagged(item) {
      this.tagged_head_temp_data = null;
      for (let i = 0; i < this.tagged_head_data.length; i++) {
        if (this.tagged_head_data[i].head_user.usID == item.head_user.usID) {
          if (!item.opcrPapTargetDistributedTaggingID) {
            this.tagged_head_data.splice(i, 1);
          } else {
            this.untagList.push(this.tagged_head_data[i]);
            this.tagged_head_data.splice(i, 1);
          }
        }
      }
    },

    edit_tag(item, index) {
      this.getHeadUsers();
      console.log(item, index);

      // console.log("TEMPHEAD", tempTaggedHeads);
      setTimeout(() => {
        let tempTaggedHeads = [];
        for (let i = 0; i < this.tagged_head_data.length; i++) {
          if (this.tagged_head_data[i].head_user.usID != item.head_user.usID) {
            tempTaggedHeads.push(this.tagged_head_data[i]);
          }
        }
        this.tagIdxToUpdate = index;
        this.tagHeadAction = "Update";
        let list = this.getMyFilteredUserList();
        if (tempTaggedHeads.length > 0) {
          for (let i = 0; i < tempTaggedHeads.length; i++) {
            for (let j = 0; j < list.length; j++) {
              if (tempTaggedHeads[i].head_user.usID == list[j].usID) {
                list.splice(j, 1);
                break;
              }
            }
          }
          this.tempHeadsList = list;
        }
        // this.tempHeadsList = list;
        this.head_target = item.target;
        if (typeof item.head_user.usID == "number") {
          this.tagged_head_temp_data = item.head_user.usID.toString();
        } else {
          this.tagged_head_temp_data = item.head_user.usID;
        }

        this.headsDialog = true;
      }, 100);
    },

    untag(item) {
      this.unTagUser = item;
      this.unTagDialog = true;
    },

    confirmUntag() {
      // console.log(this.unTagUser);
      this.removeTagged(this.unTagUser);
      this.unTagUser = null;
      this.unTagDialog = false;
    },
    cancelUntag() {
      this.unTagUser = null;
      this.unTagDialog = false;
    },

    closeHeadsDialog() {
      this.tagHeadAction = null;
      this.headsDialog = false;
    },

    saveTaggedHeads() {
      if (this.isTargetNeedForTagging) {
        let todistribute = this.efficiency;

        if (this.getCurrentTaggedTotal <= todistribute) {
          let tempT =
            parseInt(this.getCurrentTaggedTotal) + parseInt(this.head_target);
          if (tempT <= todistribute) {
            for (let j = 0; j < this.usersList.length; j++) {
              if (this.tagged_head_temp_data == this.usersList[j].usID) {
                this.tagged_head_data.push({
                  head_user: this.usersList[j],
                  target: this.head_target,
                });
                break;
              }
            }
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message =
              "Total tagged targets exceeded the target to distribute!";
          }
        } else {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.header = "System Message";
          this.fadeAwayMessage.message =
            "Total tagged targets exceeded thetarget to distribute!";
        }
      } else {
        // console.log("dreasdad", Array.isArray(this.tagged_head_data));
        // if (Array.isArray(this.tagged_head_data) == false) {
        //   this.tagged_head_data = [];
        // }
        for (let j = 0; j < this.usersList.length; j++) {
          if (this.tagged_head_temp_data == this.usersList[j].usID) {
            this.tagged_head_data.push({
              head_user: this.usersList[j],
              target: null,
            });
            break;
          }
        }
        console.log(this.tagged_head_data);
      }

      this.closeHeadsDialog();
    },

    updateTaggedHeads() {
      for (let j = 0; j < this.usersList.length; j++) {
        if (this.tagged_head_temp_data == this.usersList[j].usID) {
          this.tagged_head_data[this.tagIdxToUpdate].head_user =
            this.usersList[j];
          this.tagged_head_data[this.tagIdxToUpdate].target = this.head_target;
          break;
        }
      }
      this.tagIdxToUpdate = null;
      this.closeHeadsDialog();
    },

    checkTagging() {
      let valid = false;

      let taggingArr = ["dean", "unit_head", "prog_chair"];

      if (taggingArr.includes(this.myTaggingType.type)) {
        valid = true;
      } else {
        let todistribute = this.efficiency;

        if (this.getCurrentTaggedTotal == todistribute) {
          valid = true;
        }
      }

      return valid;
    },

    addnew() {
      if (this.$refs.PerformanceTarget.validate()) {
        // this.isSubmitLoading = true;
        let data = {
          opcrID: this.opcrID,
          mfo: this.mfo,
          QL: this.efficiency,
          QL_type: this.efficiency_type,
          efficiency: this.efficiency,
          efficiency_type: this.efficiency_type,
          quality_util_ID: this.quality,
          QT: this.getQualityDesc,
          timeliness: this.timelinessPreview,
          t_num: this.t_num,
          t_add_desc: this.t_add_desc,
          timeliness_util_id: this.timeliness_selected,
          QL_is_rated: this.eff_is_rated,
          QT_is_rated: this.qly_is_rated,
          timeliness_is_rated: this.timeliness_is_rated,
          linked_sptID: this.sptID,
          myTaggingType: this.myTaggingType.type,
          tagged_head_data: JSON.stringify(this.tagged_head_data),
          success_indicator: this.success_indicator_to_save
            ? this.success_indicator_to_save
            : this.suc_ind_preview,
        };
        this.axiosCall("/opcr-target", "POST", data).then((res) => {
          console.log(res);
          if (res.data.status == 201) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;

            this.closeD();
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
      }
    },

    update() {
      // console.log("Update Data");
      if (this.$refs.PerformanceTarget.validate()) {
        this.isSubmitLoading = true;
        let data = {
          opcrID: this.opcrID,
          mfo: this.mfo,
          QL: this.efficiency,
          QL_type: this.efficiency_type,
          efficiency: this.efficiency,
          efficiency_type: this.efficiency_type,
          quality_util_ID: this.quality,
          QT: this.getQualityDesc,
          timeliness: this.timelinessPreview,
          t_num: this.t_num,
          t_add_desc: this.t_add_desc,
          timeliness_util_id: this.timeliness_selected,
          QL_is_rated: this.eff_is_rated,
          QT_is_rated: this.qly_is_rated,
          timeliness_is_rated: this.timeliness_is_rated,
          linked_sptID: this.sptID,
          myTaggingType: this.myTaggingType.type,
          tagged_head_data: JSON.stringify(this.tagged_head_data),
          opcrPapTargetDistributedTaggingID:
            this.data.opcr_pap_target_distributed_tagging_ID,
          untagList: JSON.stringify(this.untagList),
          success_indicator: this.success_indicator_to_save
            ? this.success_indicator_to_save
            : this.suc_ind_preview,
        };

        console.log("DATA", data, this.isTargetNeedForTagging);
        // if (this.isTargetNeedForTagging && ) {
        // if (this.checkTagging()) {
        // console.log(data);
        this.axiosCall("/opcr-target/" + this.id, "PATCH", data).then((res) => {
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.closeD();
          } else {
            this.isSubmitLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
        });
        // } else {
        //   this.isSubmitLoading = false;
        //   this.fadeAwayMessage.show = true;
        //   this.fadeAwayMessage.type = "error";
        //   this.fadeAwayMessage.header = "System Message";
        //   this.fadeAwayMessage.message =
        //     "Please tag director/heads accordingly!";
        // }
        // } else {
        //   let opcr_data = JSON.stringify({
        //     id: this.id,
        //     mfo: this.mfo,
        //     QL: this.efficiency,
        //     QL_type: this.efficiency_type,
        //     efficiency: this.efficiency,
        //     efficiency_type: this.efficiency_type,
        //     quality_util_ID: this.quality,
        //     QT: this.getQualityDesc,
        //     timeliness: this.timelinessPreview,
        //     t_num: this.t_num,
        //     t_add_desc: this.t_add_desc,
        //     timeliness_util_id: this.timeliness_selected,
        //     QL_is_rated: this.eff_is_rated,
        //     QT_is_rated: this.qly_is_rated,
        //     timeliness_is_rated: this.timeliness_is_rated,
        //     success_indicator: this.suc_ind_preview,
        //   });

        //   let data = {
        //     taggingType: this.myTaggingType.type,
        //     opcrPapTargetDistributedTaggingID: this.data
        //       .opcr_pap_target_distributed_tagging_ID
        //       ? this.data.opcr_pap_target_distributed_tagging_ID
        //       : null,
        //     idToTag: this.data.id,
        //     tagged_head_data: JSON.stringify(this.tagged_head_data),
        //     typeOfTarget: "cf",
        //     untagList: JSON.stringify(this.untagList),
        //     opcr_data: opcr_data,
        //   };

        //   this.axiosCall(
        //     "/opcr-target/save-opcr-to-ipcr-tagging",
        //     "POST",
        //     data,
        //   ).then((res) => {
        //     if (res.data.status == 201) {
        //       this.fadeAwayMessage.show = true;
        //       this.fadeAwayMessage.type = "success";
        //       this.fadeAwayMessage.header = "System Message";
        //       this.fadeAwayMessage.message = res.data.msg;
        //       this.closeD();
        //     } else {
        //       this.fadeAwayMessage.show = true;
        //       this.fadeAwayMessage.type = "error";
        //       this.fadeAwayMessage.header = "System Message";
        //       this.fadeAwayMessage.message = res.data.msg;
        //       this.taggingBtnLoading = false;
        //     }
        //   });
        // }
      }
    },

    getTaggedOffices(id, opcrPapTrgtDistId) {
      console.log(
        "TAGGED OFFICES",
        id,
        opcrPapTrgtDistId,
        this.myTaggingType.type,
      );
      if (
        // this.myTaggingType.type == "prog_chair" ||
        this.myTaggingType.type == "unit_head"
      ) {
        this.axiosCall(
          "/opcr-target/getTaggedPersonnel/" +
            opcrPapTrgtDistId +
            "/" +
            this.myTaggingType.type +
            "/cf",
          "GET",
        ).then((res) => {
          let data = res.data;
          console.log("asdasd", res.data);
          for (let i = 0; i < data.length; i++) {
            data[i].head_user.name = this.toTitleCase(data[i].head_user.name);
          }

          this.tagged_head_data = data;
        });
      } else {
        this.axiosCall(
          "/opcr-target/getTaggedOffices/" +
            id +
            "/" +
            this.myTaggingType.type +
            "/cf",
          "GET",
        ).then((res) => {
          console.log("DEANS SAMTING", res.data);
          let data = res.data;
          for (let i = 0; i < data.length; i++) {
            data[i].head_user.name = this.toTitleCase(data[i].head_user.name);
          }
          this.tagged_head_data = data;
        });
      }
    },

    getIPCRQuality() {
      this.axiosCall("/ipcr-quality-util", "GET").then((res) => {
        this.qlyList = res.data;
      });
    },

    getIPCRTimeliness() {
      this.axiosCall("/ipcr-timeliness-util", "GET").then((res) => {
        this.timelinessList = res.data;
      });
    },

    getEmpDetails() {
      this.axiosCall("/employee/my-details", "GET").then((res) => {
        console.log(res.data);
        this.assigned_office = res.data.officeID;
      });
    },

    getHeadUsers() {
      this.axiosCall(
        "/user-details/getUsersHeadProgChairAbove_Office",
        "GET",
      ).then((res) => {
        let data = res.data;
        data.forEach((element, i) => {
          data[i].name = this.toTitleCase(element.name);
        });
        this.usersList = data;
      });
    },

    getOffices() {
      this.axiosCall("/offices", "GET").then((res) => {
        this.officeList = res.data;
      });
    },

    getDesignations() {
      this.axiosCall("/designations", "GET").then((res) => {
        this.designationList = res.data;
      });
    },
  },
};
</script>
